import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, clearToken } from '../lib/api';
import { useMe } from '../lib/me';
import { MARKETING_URL } from '../env';
import DashboardLayout from '../components/DashboardLayout';
import type { BillingStatusResponse, PaddleConfigResponse, PlanId } from '@quicktl/shared';
import { PLAN_LIMITS } from '@quicktl/shared';

declare global {
  interface Window {
    Paddle?: {
      Environment: { set: (env: string) => void };
      Initialize: (config: { token: string; eventCallback?: (event: { name: string; data?: any }) => void }) => void;
      Checkout: {
        open: (config: {
          items: { priceId: string; quantity: number }[];
          customer?: { email: string };
          customData?: Record<string, string>;
        }) => void;
      };
    };
  }
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  trialing: { label: 'Free trial', color: 'bg-blue-500/10 border-blue-500/20 text-blue-400' },
  trial_read_only: { label: 'Trial expired — read-only', color: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' },
  trial_expired: { label: 'Trial expired', color: 'bg-red-500/10 border-red-500/20 text-red-400' },
  active: { label: 'Active', color: 'bg-green-500/10 border-green-500/20 text-green-400' },
  past_due: { label: 'Past due', color: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' },
  past_due_read_only: { label: 'Past due — read-only', color: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' },
  past_due_blocked: { label: 'Suspended', color: 'bg-red-500/10 border-red-500/20 text-red-400' },
  canceled: { label: 'Canceled', color: 'bg-slate-500/10 border-slate-500/20 text-slate-400' },
  exempt: { label: 'Exempt', color: 'bg-green-500/10 border-green-500/20 text-green-400' },
};

const PLAN_DISPLAY: Record<PlanId, { name: string; price: string }> = {
  starter: { name: 'Starter', price: '$5/mo' },
  pro: { name: 'Pro', price: '$25/mo' },
};

export default function Billing() {
  const navigate = useNavigate();
  const { me } = useMe();
  const emailVerified = !!me?.emailVerifiedAt;

  const [searchParams, setSearchParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingStatusResponse | null>(null);
  const [paddleConfig, setPaddleConfig] = useState<PaddleConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paddleReady, setPaddleReady] = useState(false);
  const [success, setSuccess] = useState('');
  const [activating, setActivating] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handlePaddleEvent = useCallback((event: { name: string; data?: any }) => {
    if (event.name === 'checkout.completed') {
      setSuccess('Payment received — activating your subscription…');
      setActivating(true);
      pollForSubscription();
    }
  }, []);

  function pollForSubscription() {
    if (pollRef.current) clearInterval(pollRef.current);

    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts++;
      try {
        const status = await api.getBillingStatus();
        if (status.subscription) {
          if (pollRef.current) clearInterval(pollRef.current);
          setBilling(status);
          setActivating(false);
          setSuccess('Your subscription is active!');
        } else if (attempts >= 30) {
          if (pollRef.current) clearInterval(pollRef.current);
          setActivating(false);
          setSuccess('Payment received — subscription activation may take a few moments. Refresh the page to check.');
        }
      } catch {
        // Keep polling on error
      }
    }, 2000);
  }

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const [status, config] = await Promise.all([
          api.getBillingStatus(),
          api.getPaddleConfig().catch(() => null),
        ]);
        setBilling(status);
        setPaddleConfig(config);

        if (config) {
          loadPaddleJs(config);
        }

        // Returning from a redirect-based checkout
        if (searchParams.has('_ptxn')) {
          setSuccess('Payment received — activating your subscription…');
          setActivating(true);
          setSearchParams({}, { replace: true });
          pollForSubscription();
        }
      } catch (e: any) {
        if (e.status === 401) {
          clearToken();
          navigate('/login');
          return;
        }
        setError(e.body?.error || 'Failed to load billing status');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function loadPaddleJs(config: PaddleConfigResponse) {
    if (window.Paddle) {
      if (config.environment === 'sandbox') window.Paddle.Environment.set('sandbox');
      window.Paddle.Initialize({ token: config.clientToken, eventCallback: handlePaddleEvent });
      setPaddleReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (config.environment === 'sandbox') window.Paddle?.Environment.set('sandbox');
      window.Paddle?.Initialize({ token: config.clientToken, eventCallback: handlePaddleEvent });
      setPaddleReady(true);
    };
    document.head.appendChild(script);
  }

  function openCheckout(plan: PlanId) {
    if (!paddleConfig || !window.Paddle) return;

    const priceId = plan === 'starter' ? paddleConfig.starterPriceId : paddleConfig.proPriceId;
    window.Paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      customer: { email: billing?.email || '' },
      customData: { accountId: billing?.accountId || '' },
    });
  }

  async function handleChangePlan(plan: PlanId) {
    setChangingPlan(true);
    setError('');
    setSuccess('');
    try {
      await api.changePlan(plan);
      const status = await api.getBillingStatus();
      setBilling(status);
      setSuccess(`Switched to ${plan === 'pro' ? 'Pro' : 'Starter'} plan.`);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  }

  async function handleCancel() {
    if (!confirm('Are you sure you want to cancel your subscription? You\'ll retain access until the end of your billing period.')) return;
    setCanceling(true);
    setError('');
    try {
      await api.cancelSubscription();
      const status = await api.getBillingStatus();
      setBilling(status);
      setSuccess('Your subscription will be canceled at the end of the billing period.');
    } catch (e: any) {
      setError(e.body?.error || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  }

  const statusInfo = billing ? STATUS_LABELS[billing.billingStatus] || STATUS_LABELS.canceled : null;
  const showPlans = billing && !billing.subscription && billing.billingStatus !== 'exempt';
  const showUpgrade = billing?.subscription?.plan === 'starter';
  const showCancel = billing?.subscription && !billing.subscription.canceledAt && billing.billingStatus !== 'canceled';

  async function handleResendVerification() {
    try {
      await api.resendVerificationEmail();
      setSuccess(`Verification email sent to ${me?.email}. Check your inbox.`);
    } catch (e: any) {
      setError(e.body?.error || 'Failed to send verification email.');
    }
  }

  const verifyEmailGate = (
    <div className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/10">
      <p className="text-sm text-amber-200">
        Please verify your email address before subscribing or changing plans.
      </p>
      <button
        onClick={handleResendVerification}
        className="mt-3 text-sm font-medium text-amber-200 hover:text-amber-100 underline"
      >
        Resend verification email
      </button>
    </div>
  );

  return (
    <DashboardLayout>
        <h2 className="text-xl font-semibold mb-6">Billing</h2>

        {success && (
          <div role="alert" className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm flex items-center gap-2">
            {activating && (
              <svg className="animate-spin h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {success}
          </div>
        )}

        {error && (
          <div role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-slate-500 text-sm" aria-live="polite">Loading…</p>
        ) : billing ? (
          <div className="space-y-8">
            {/* Current status */}
            <section>
              <h3 className="text-sm font-medium text-slate-300 mb-3">Status</h3>
              <div className="p-4 rounded-lg border border-slate-800">
                <div className="flex items-center gap-3">
                  {statusInfo && (
                    <span className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium border ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  )}
                  {billing.subscription && (
                    <span className="text-sm text-slate-300">
                      {PLAN_DISPLAY[billing.subscription.plan]?.name} plan — {PLAN_DISPLAY[billing.subscription.plan]?.price}
                    </span>
                  )}
                </div>

                {billing.trialEndsAt && !billing.subscription && (
                  <p className="mt-3 text-sm text-slate-400">
                    {billing.billingStatus === 'trialing' ? (
                      <>Trial ends <strong className="text-slate-200">{new Date(billing.trialEndsAt).toLocaleDateString()}</strong></>
                    ) : (
                      <>Trial ended {new Date(billing.trialEndsAt).toLocaleDateString()}</>
                    )}
                  </p>
                )}

                {billing.subscription?.currentPeriodEnd && (
                  <p className="mt-3 text-sm text-slate-400">
                    Current period ends {new Date(billing.subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}

                {billing.subscription?.canceledAt && (
                  <p className="mt-2 text-sm text-yellow-400">
                    Cancels at end of billing period. Want to resubscribe? <a href={`${MARKETING_URL}/contact`} className="text-emerald-400 hover:text-emerald-300 transition-colors">Contact us</a>.
                  </p>
                )}
              </div>
            </section>

            {/* Plan limits */}
            {billing.planLimits && (
              <section>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Plan limits</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border border-slate-800 text-center">
                    <p className="text-2xl font-bold">{billing.planLimits.maxProjects}</p>
                    <p className="text-xs text-slate-500 mt-1">Projects</p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-800 text-center">
                    <p className="text-2xl font-bold">{billing.planLimits.maxKeysPerProject.toLocaleString()}</p>
                    <p className="text-xs text-slate-500 mt-1">Keys per project</p>
                  </div>
                  <div className="p-4 rounded-lg border border-slate-800 text-center">
                    <p className="text-2xl font-bold">{billing.planLimits.maxLocalesPerProject}</p>
                    <p className="text-xs text-slate-500 mt-1">Locales per project</p>
                  </div>
                </div>
              </section>
            )}

            {/* Plan selection (for trial/expired/canceled users) */}
            {showPlans && (
              <section>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Choose a plan</h3>
                {!emailVerified ? verifyEmailGate : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <PlanCard
                        name="Starter"
                        price="$5"
                        limits={PLAN_LIMITS.starter}
                        onSelect={() => openCheckout('starter')}
                        disabled={!paddleReady}
                      />
                      <PlanCard
                        name="Pro"
                        price="$25"
                        limits={PLAN_LIMITS.pro}
                        highlighted
                        onSelect={() => openCheckout('pro')}
                        disabled={!paddleReady}
                      />
                    </div>
                    {!paddleReady && paddleConfig && (
                      <p className="mt-2 text-xs text-slate-500">Loading payment provider…</p>
                    )}
                    {!paddleConfig && (
                      <p className="mt-2 text-xs text-slate-500">Billing is not yet configured. Contact support.</p>
                    )}
                  </>
                )}
              </section>
            )}

            {/* Upgrade option for Starter users */}
            {showUpgrade && (
              <section>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Upgrade</h3>
                {!emailVerified ? verifyEmailGate : (
                  <div className="p-4 rounded-lg border border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-300">
                          Upgrade to <strong>Pro</strong> for more projects, keys, and locales.
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {PLAN_LIMITS.pro.maxProjects} projects · {PLAN_LIMITS.pro.maxKeysPerProject.toLocaleString()} keys · {PLAN_LIMITS.pro.maxLocalesPerProject} locales — $25/mo
                        </p>
                      </div>
                      <button
                        onClick={() => handleChangePlan('pro')}
                        disabled={changingPlan}
                        className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                      >
                        {changingPlan ? 'Upgrading…' : 'Upgrade'}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Cancel subscription */}
            {showCancel && (
              <section>
                <h3 className="text-sm font-medium text-slate-300 mb-3">Cancel subscription</h3>
                <div className="p-4 rounded-lg border border-slate-800">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-400">
                      Cancel your subscription. You'll retain access until the end of your current billing period.
                    </p>
                    <button
                      onClick={handleCancel}
                      disabled={canceling}
                      className="shrink-0 ml-4 text-sm text-red-400 hover:text-red-300 disabled:opacity-50 transition-colors"
                    >
                      {canceling ? 'Canceling…' : 'Cancel subscription'}
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* Trial extension / contact */}
            {(billing.billingStatus === 'trial_read_only' || billing.billingStatus === 'trial_expired') && (
              <section>
                <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/50">
                  <p className="text-sm text-slate-300">
                    Need more time to evaluate? <a href={`${MARKETING_URL}/contact`} className="text-emerald-400 hover:text-emerald-300 transition-colors">Contact us</a> to extend your trial.
                  </p>
                </div>
              </section>
            )}
          </div>
        ) : null}
    </DashboardLayout>
  );
}

function PlanCard({
  name,
  price,
  limits,
  highlighted,
  onSelect,
  disabled,
}: {
  name: string;
  price: string;
  limits: { maxProjects: number; maxKeysPerProject: number; maxLocalesPerProject: number };
  highlighted?: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={`p-5 rounded-lg border ${highlighted ? 'border-emerald-500 ring-1 ring-emerald-500/20' : 'border-slate-800'}`}>
      <h4 className="text-base font-semibold">{name}</h4>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-2xl font-bold">{price}</span>
        <span className="text-sm text-slate-500">/mo</span>
      </div>
      <ul className="mt-4 space-y-2 text-sm text-slate-400">
        <li>{limits.maxProjects} projects</li>
        <li>{limits.maxKeysPerProject.toLocaleString()} keys per project</li>
        <li>{limits.maxLocalesPerProject} locales per project</li>
        <li>Unlimited translation memory</li>
        <li>CLI + API access</li>
      </ul>
      <button
        onClick={onSelect}
        disabled={disabled}
        className={`mt-5 w-full rounded-lg px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
          highlighted
            ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
            : 'bg-slate-800 text-slate-100 hover:bg-slate-700'
        }`}
      >
        Subscribe
      </button>
    </div>
  );
}
